import { emoteLabel } from '../emotes'
import { RunnerPreview } from '../runnerPreview/RunnerPreview'
import type { RunnerSlot } from '../runnerPreview/roster'
import type { Seat } from '../session'
import { SEAT_TINTS, uiArt } from './art'
import styles from './pedestal.module.css'

const PEDESTAL_ART = uiArt('ui_pedestal')

interface Props {
  /** Seat index 0–3: the tint and the left-to-right place. */
  index: number
  seat: Seat | undefined
  /** The seat's runner (`seatRunner`), or the faint silhouette's runner when empty. */
  runner: RunnerSlot
  /** Shown as `· Lv N` after the name (yourself only until the roster carries levels, O-21). */
  level: number | null
  emote: number | null
}

/** The stone (A-5), tinted by seat; a CSS shape while the art is missing. */
function Stone() {
  return (
    <div class={styles.stone} aria-hidden="true">
      {PEDESTAL_ART ? (
        <>
          <img class={styles.stoneImg} src={PEDESTAL_ART} alt="" draggable={false} />
          <span class={styles.stoneTint} style={{ '--art': `url('${PEDESTAL_ART}')` }} />
        </>
      ) : (
        <span class={styles.stonePlaceholder} />
      )}
    </div>
  )
}

/**
 * One matchmaking pedestal (PHASE3 T3.2): empty, a faint runner silhouette and "Waiting…"; taken,
 * the seat's runner drops on over 300 ms with a dust puff under a `name · Lv N` tag. A received
 * emote shows as a bubble over the tag.
 */
export function Pedestal({ index, seat, runner, level, emote }: Props) {
  const tint = SEAT_TINTS[index % SEAT_TINTS.length]
  return (
    <li
      class={`${styles.pedestal} ${seat ? '' : styles.empty} ${seat?.isMe ? styles.me : ''}`}
      style={{ '--tint': tint }}
      data-testid={`pedestal-${index}`}
    >
      {seat ? (
        // Keyed by who sits here, so a new arrival drops in again.
        <div key={`${seat.name}|${seat.charId}`} class={styles.filled}>
          <div class={styles.tag}>
            <span class={styles.tagName}>{seat.name}</span>
            {level !== null && <span class={styles.tagLevel}> · Lv {level}</span>}
            {(seat.isMe || seat.isHost) && (
              <span class={styles.tagBadges}>
                {[seat.isMe && 'You', seat.isHost && 'Host'].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          <div class={`${styles.runner} ${styles.drop}`}>
            <RunnerPreview slot={runner} />
          </div>
          <span class={styles.dust} aria-hidden="true" />
        </div>
      ) : (
        <div class={styles.filled}>
          <div class={`${styles.tag} ${styles.tagWaiting}`}>Waiting…</div>
          <div class={`${styles.runner} ${styles.silhouette}`} aria-hidden="true">
            <RunnerPreview slot={runner} />
          </div>
        </div>
      )}
      {emote !== null && <span class={styles.bubble}>{emoteLabel(emote)}</span>}
      <Stone />
    </li>
  )
}
